import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import Marquee from "@/components/Marquee";
import { Reveal, Eyebrow, RevealLines, EASE } from "@/components/Reveal";
import { GroupMark } from "@/components/Logo";
import Seo from "@/components/Seo";

const HERO_VIDEO = "https://videos.pexels.com/video-files/10135156/10135156-hd_1920_1080_30fps.mp4";

const IMG = {
  poster: "https://images.unsplash.com/photo-1776362355123-ca966d36e29c?q=80&w=2400&auto=format&fit=crop",
  presentazione: "https://images.unsplash.com/photo-1776362355123-ca966d36e29c?q=80&w=1600&auto=format&fit=crop",
  vendita: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?q=80&w=1800&auto=format&fit=crop",
  locazione: "https://images.unsplash.com/photo-1519710164239-da123dc03ef4?q=80&w=1800&auto=format&fit=crop",
};

const HLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Fade = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

const GROUP = [
  { key: "estate", full: "IDEAL REAL ESTATE", tag: "Compravendita e locazioni immobiliari.", to: "/" },
  { key: "rent", full: "IDEAL RENT", tag: "Short rent & property management.", to: "/il-gruppo" },
  { key: "ready", full: "IDEAL READY", tag: "Manutenzione e pronto intervento.", to: "/il-gruppo" },
  { key: "construction", full: "IDEAL RISTRUTTURA", tag: "Ristrutturazione e costruzione.", to: "/il-gruppo" },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const parallaxScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  const [properties, setProperties] = useState(null);
  useEffect(() => {
    api.get("/properties")
      .then((r) => setProperties(r.data))
      .catch(() => setProperties([]));
  }, []);

  const sale = (properties || []).filter((p) => p.status === "vendita");
  const featured = sale.slice(0, 5);

  return (
    <div data-testid="home-page">
      <Seo
        title="IDEAL REAL ESTATE — Agenzia immobiliare a Milano e Lombardia"
        description="Consulenza immobiliare, compravendite e locazioni a Milano e in tutta la Lombardia. Valutazioni gratuite e assistenza personalizzata da IDEAL REAL ESTATE."
        path="/"
      />
      {/* ============ HERO ============ */}
      <section ref={heroRef} className="relative flex min-h-[100svh] flex-col overflow-hidden bg-[#101317] text-sand">
        <motion.div style={{ y: parallaxY, scale: parallaxScale }} className="absolute inset-0">
          <motion.div
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.8, ease: EASE }}
            className="h-full w-full"
          >
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              poster={IMG.poster}
              data-testid="hero-video"
              className="h-full w-full object-cover brightness-[1.08]"
            >
              <source src="https://videos.pexels.com/video-files/34955072/14807039_2560_1440_50fps.mp4" type="video/mp4" />
            </video>
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#101317]/45 via-[#101317]/20 to-[#101317]/90" />
        </motion.div>

        <div className="relative mx-auto flex w-full max-w-[1440px] flex-1 flex-col justify-end px-6 pb-16 pt-36 lg:px-12">
          <h1 className="mt-2 font-serif text-[13.5vw] font-medium leading-[0.98] text-sand sm:text-[11vw] lg:text-[6.9rem]">
            <HLine delay={0.4}><span className="font-light italic">Dove <span className="text-[#D9BC8C]">l'ideale</span></span></HLine>
            <HLine delay={0.55}><span className="font-light italic">prende forma.</span></HLine>
          </h1>

          <div className="mt-12 flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
            <Fade delay={0.85}>
              <p className="max-w-md text-base font-light leading-relaxed text-sand/85 [text-shadow:_0_1px_18px_rgba(16,19,23,0.6)] lg:text-lg">
                Consulenza immobiliare, compravendite e locazioni a Milano e in tutta la Lombardia.
              </p>
            </Fade>
            <Fade delay={1} className="flex flex-wrap gap-4">
              <Link
                to="/contatti?motivo=Vendita"
                data-testid="hero-btn-sell"
                className="inline-flex items-center gap-3 rounded-full bg-sand px-8 py-4 text-[12px] font-medium uppercase tracking-wide2 text-ink transition-colors duration-300 hover:bg-champagne hover:text-sand"
              >
                Vendi la tua casa
              </Link>
              <Link
                to="/vendita"
                data-testid="hero-btn-find"
                className="inline-flex items-center gap-3 rounded-full border border-sand/40 px-8 py-4 text-[12px] font-medium uppercase tracking-wide2 text-sand transition-all duration-300 hover:border-sand hover:bg-sand/10"
              >
                Trova la tua prossima casa
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Fade>
          </div>

          <Fade delay={1.15} className="mt-14 border-t border-sand/15 pt-6">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 font-mono text-[11px] uppercase tracking-[0.18em] text-sand/50">
              <span>Compravendite</span><span className="text-champagne">◆</span>
              <span>Locazioni</span><span className="text-champagne">◆</span>
              <span>Valutazioni</span><span className="text-champagne">◆</span>
              <span>Consulenza</span>
              <span className="ml-auto hidden lg:inline">Parte di IDEAL GROUP</span>
            </div>
          </Fade>
        </div>
      </section>

      {/* ============ MARQUEE ============ */}
      <Marquee items={["Compravendite", "Locazioni", "Valutazioni gratuite", "Consulenza immobiliare", "Milano", "Lombardia"]} />

      {/* ============ PRESENTAZIONE ============ */}
      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-36">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <Reveal><Eyebrow>Chi siamo</Eyebrow></Reveal>
            <RevealLines
              lines={["Un partner di fiducia", "per le scelte immobiliari", "che contano."]}
              className="mt-6 font-serif text-4xl font-medium leading-[1.05] lg:text-6xl"
            />
            <Reveal delay={0.15}>
              <p className="mt-12 max-w-xl text-base font-light leading-relaxed text-ink2 lg:text-lg">
                IDEAL REAL ESTATE accompagna proprietari, acquirenti, investitori e locatori
                nelle loro scelte immobiliari, offrendo consulenza personalizzata per la
                compravendita e la locazione di immobili residenziali.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-mut lg:text-lg">
                Operiamo a Milano e in tutta la Lombardia, con un approccio orientato alla
                conoscenza del mercato, alla valorizzazione degli immobili e alla costruzione
                di relazioni professionali durature.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link
                to="/il-gruppo"
                className="group mt-12 inline-flex items-center gap-3 text-[12px] font-medium uppercase tracking-wide2 text-ink transition-colors hover:text-champagne"
              >
                <span className="border-b border-ink/30 pb-1 transition-colors group-hover:border-champagne">Scopri IDEAL GROUP</span>
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.2} className="relative">
              <div className="overflow-hidden">
                <img
                  src={IMG.presentazione}
                  alt="Architettura contemporanea a Milano"
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover transition-transform duration-[1400ms] ease-out hover:scale-[1.04]"
                />
              </div>
              <div className="absolute -bottom-8 -left-2 bg-ink px-8 py-6 text-sand lg:-left-10">
                <p className="font-serif text-2xl italic">Dove l'ideale prende forma.</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-sand/50">IDEAL REAL ESTATE</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ SELEZIONE ============ */}
      <section className="border-t border-ink/10 bg-sand2" data-testid="home-featured-properties">
        <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <Reveal><Eyebrow>Portafoglio esclusivo</Eyebrow></Reveal>
              <RevealLines
                lines={["Selezione d'eccellenza", "Milano & Lombardia."]}
                className="mt-5 font-serif text-4xl font-medium leading-[1.05] lg:text-5xl"
              />
            </div>
            <Reveal delay={0.2}>
              <Link
                to="/vendita"
                className="group inline-flex items-center gap-3 text-[12px] font-medium uppercase tracking-wide2 text-ink transition-colors hover:text-champagne"
              >
                <span className="border-b border-ink/30 pb-1 group-hover:border-champagne">Tutti gli immobili</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>

          {properties === null ? (
            <div className="mt-16 grid gap-8 lg:grid-cols-12">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={`animate-pulse bg-sand3 ${i < 2 ? "lg:col-span-6" : "lg:col-span-4"} aspect-[4/3]`} />
              ))}
            </div>
          ) : featured.length > 0 ? (
            <div className="mt-16 grid gap-x-8 gap-y-16 lg:grid-cols-12">
              {featured.map((p, i) => (
                <Reveal
                  key={p.code}
                  delay={(i % 3) * 0.08}
                  className={i === 0 ? "lg:col-span-7" : i === 1 ? "lg:col-span-5" : "lg:col-span-4"}
                >
                  <PropertyCard property={p} wide={i === 0} />
                </Reveal>
              ))}
            </div>
          ) : (
            <p className="mt-16 font-serif text-2xl italic text-mut">Il portafoglio viene aggiornato a breve.</p>
          )}
        </div>
      </section>

      {/* ============ SERVIZI ============ */}
      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <div className="grid gap-8 lg:grid-cols-2">
          {[
            {
              img: IMG.vendita,
              title: "Compravendita",
              desc: "Ti affianchiamo nella vendita e nell'acquisto di immobili residenziali, con una consulenza costruita attorno ai tuoi obiettivi.",
              to: "/vendita",
              cta: "Immobili in vendita",
            },
            {
              img: IMG.locazione,
              title: "Locazioni",
              desc: "Contratti transitori, locazioni di medio-lungo periodo e soluzioni per studenti e professionisti: la casa giusta, al momento giusto.",
              to: "/locazione",
              cta: "Immobili in locazione",
            },
          ].map((s, i) => (
            <Reveal key={s.title} delay={i * 0.12}>
              <Link to={s.to} className="group relative block overflow-hidden" data-testid={`home-service-${s.title.toLowerCase()}`}>
                <img
                  src={s.img}
                  alt={s.title}
                  loading="lazy"
                  className="h-[420px] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05] lg:h-[540px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent transition-opacity duration-500" />
                <div className="absolute inset-x-0 bottom-0 p-8 lg:p-12">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sand/60">0{i + 1}</p>
                  <h3 className="mt-3 font-serif text-4xl font-medium text-sand">{s.title}</h3>
                  <p className="mt-3 max-w-md text-[15px] font-light leading-relaxed text-sand/70">{s.desc}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[12px] font-medium uppercase tracking-wide2 text-sand">
                    {s.cta}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ VALUTAZIONE ============ */}
      <section className="bg-ink text-sand">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-10 px-6 py-24 lg:flex-row lg:items-center lg:justify-between lg:px-12 lg:py-32">
          <div>
            <Reveal><Eyebrow light>Per i proprietari</Eyebrow></Reveal>
            <RevealLines
              lines={["Vuoi conoscere il valore reale", "del tuo immobile?"]}
              className="mt-5 font-serif text-4xl font-medium leading-[1.05] lg:text-5xl"
              lineClass=""
            />
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-sand/60">
                I nostri esperti effettuano valutazioni puntuali, basate sull'andamento del
                mercato immobiliare milanese e lombardo. Un servizio gratuito e senza impegno.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.25} className="shrink-0">
            <Link
              to="/contatti?motivo=Valutazione"
              data-testid="home-valuation-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-sand px-9 py-4 text-[12px] font-medium uppercase tracking-wide2 text-ink transition-colors duration-300 hover:bg-champagne hover:text-sand"
            >
              Richiedi una valutazione gratuita
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ============ GROUP TEASER ============ */}
      <section className="bg-sand2">
        <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
          <div className="max-w-2xl">
            <Reveal><Eyebrow>IDEAL GROUP</Eyebrow></Reveal>
            <RevealLines
              lines={["Un unico gruppo,", "competenze complementari."]}
              className="mt-5 font-serif text-4xl font-medium leading-[1.05] lg:text-5xl"
            />
          </div>
          <div className="mt-16 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-4">
            {GROUP.map((g, i) => (
              <Reveal key={g.key} delay={i * 0.1} className="bg-sand2">
                <Link to={g.to} className="group flex h-full flex-col p-10 transition-colors duration-500 hover:bg-sand lg:p-12">
                  <GroupMark company={g.key === "estate" ? "estate" : g.key} className="h-12 w-auto" />
                  <h3 className="mt-10 font-serif text-2xl font-semibold tracking-[0.08em]">{g.full}</h3>
                  <p className="mt-2 font-serif text-lg italic text-champagne">{g.tag}</p>
                  <span className="mt-auto inline-flex items-center gap-2 pt-10 text-[11px] font-medium uppercase tracking-wide2 text-mut transition-colors group-hover:text-champagne">
                    Scopri <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA FINALE ============ */}
      <section className="mx-auto max-w-[1440px] px-6 py-28 lg:px-12 lg:py-40">
        <Reveal><Eyebrow>Contatti</Eyebrow></Reveal>
        <RevealLines
          lines={["Parliamo del tuo", "prossimo progetto immobiliare."]}
          className="mt-6 max-w-4xl font-serif text-4xl font-medium leading-[1.05] lg:text-6xl"
        />
        <Reveal delay={0.25} className="mt-12">
          <Link
            to="/contatti"
            className="group inline-flex items-center gap-3 rounded-full bg-ink px-9 py-4 text-[12px] font-medium uppercase tracking-wide2 text-sand transition-colors duration-300 hover:bg-champagne"
          >
            Vai ai contatti
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
